/**
 * React Query hooks for feature requests: access, the requester's list, the
 * developer board, and the two-way chat thread on a request.
 *
 * Query keys carry the full selection and every queryFn forwards `signal`
 * (see src/features/leaderboards/hooks/use-leaderboards.ts).
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { useAuth } from '@/features/auth/hooks/use-auth';

import {
  closeBoardRequest,
  createFeatureRequest,
  fetchBoard,
  fetchBoardCounts,
  fetchBoardRequest,
  fetchBoardThread,
  fetchDevelopers,
  fetchMyFeatureRequest,
  fetchMyFeatureRequests,
  fetchMyThread,
  getFeatureRequestAccess,
  pickBoardRequest,
  sendBoardMessage,
  sendMyMessage,
  updateBoardRequest,
  type CreateFeatureRequestPayload,
  type FeatureBoardCounts,
  type FeatureBoardFilters,
  type FeatureRequestAccess,
  type FeatureRequestComment,
  type FeatureRequestDetail,
  type FeatureRequestPerson,
  type FeatureRequestPriority,
  type OpenFeatureRequestStatus,
  type PaginatedFeatureRequests,
} from '../services/feature-requests-service';

/** Whose view of a request: the requester's own, or the developer board. */
export type FeatureRequestSide = 'mine' | 'board';

export const featureRequestKeys = {
  all: ['feature-requests'] as const,
  access: ['feature-requests', 'my-access'] as const,
  myList: (page: number) => ['feature-requests', 'mine', 'list', page] as const,
  myListAll: ['feature-requests', 'mine', 'list'] as const,
  board: (filters: FeatureBoardFilters) => ['feature-requests', 'board', 'list', filters] as const,
  boardAll: ['feature-requests', 'board', 'list'] as const,
  counts: ['feature-requests', 'board', 'counts'] as const,
  developers: ['feature-requests', 'board', 'developers'] as const,
  detail: (side: FeatureRequestSide, number: string) => ['feature-requests', side, 'detail', number] as const,
  thread: (side: FeatureRequestSide, number: string) => ['feature-requests', side, 'thread', number] as const,
};

/** Whether the user may submit (`can_request`) and/or work the board (`can_manage`). */
export function useFeatureRequestAccess() {
  const { user } = useAuth();
  const signedIn = Boolean(user) && Boolean(localStorage.getItem('wb.authToken'));
  return useQuery<FeatureRequestAccess>({
    queryKey: featureRequestKeys.access,
    queryFn: ({ signal }) => getFeatureRequestAccess(signal),
    enabled: signedIn,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

// -- requester -----------------------------------------------------------------

export function useMyFeatureRequests(page: number) {
  return useQuery<PaginatedFeatureRequests>({
    queryKey: featureRequestKeys.myList(page),
    queryFn: ({ signal }) => fetchMyFeatureRequests(page, signal),
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  });
}

export function useCreateFeatureRequest() {
  const queryClient = useQueryClient();
  return useMutation<FeatureRequestDetail, Error, CreateFeatureRequestPayload>({
    mutationFn: createFeatureRequest,
    onSuccess: (created) => {
      queryClient.setQueryData(featureRequestKeys.detail('mine', created.request_number), created);
      void queryClient.invalidateQueries({ queryKey: featureRequestKeys.myListAll });
    },
  });
}

// -- shared: one request and its thread ------------------------------------------

/** One request from either side; disabled while `number` is null. */
export function useFeatureRequest(side: FeatureRequestSide, number: string | null) {
  return useQuery<FeatureRequestDetail>({
    queryKey: featureRequestKeys.detail(side, number ?? ''),
    queryFn: ({ signal }) =>
      side === 'mine' ? fetchMyFeatureRequest(number as string, signal) : fetchBoardRequest(number as string, signal),
    enabled: Boolean(number),
    staleTime: 30 * 1000,
    retry: false,
  });
}

function mergeMessages(current: FeatureRequestComment[], incoming: FeatureRequestComment[]): FeatureRequestComment[] {
  if (incoming.length === 0) return current;
  const seen = new Set(current.map((m) => m.id));
  return [...current, ...incoming.filter((m) => !seen.has(m.id))].sort((a, b) => a.id - b.id);
}

/**
 * The chat thread. No polling: it loads when opened, when the window regains focus,
 * after a send, and on the Refresh button. Each load asks only for messages newer
 * than the last one held (`?after=<id>`) and appends them.
 */
export function useFeatureRequestThread(side: FeatureRequestSide, number: string | null) {
  const queryClient = useQueryClient();
  return useQuery<FeatureRequestComment[]>({
    queryKey: featureRequestKeys.thread(side, number ?? ''),
    queryFn: async ({ signal }) => {
      const key = featureRequestKeys.thread(side, number as string);
      const held = queryClient.getQueryData<FeatureRequestComment[]>(key) ?? [];
      const after = held.length ? held[held.length - 1].id : 0;
      const fetcher = side === 'mine' ? fetchMyThread : fetchBoardThread;
      const incoming = await fetcher(number as string, after, signal);
      if (side === 'mine' && incoming.length) {
        // Reading clears requester_unread on the server; refresh the list dot.
        void queryClient.invalidateQueries({ queryKey: featureRequestKeys.myListAll });
      }
      return mergeMessages(held, incoming);
    },
    enabled: Boolean(number),
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
}

function refreshAfterChange(queryClient: QueryClient, side: FeatureRequestSide, number: string) {
  void queryClient.invalidateQueries({ queryKey: featureRequestKeys.detail(side, number) });
  void queryClient.invalidateQueries({ queryKey: featureRequestKeys.thread(side, number) });
  if (side === 'mine') {
    void queryClient.invalidateQueries({ queryKey: featureRequestKeys.myListAll });
  } else {
    void queryClient.invalidateQueries({ queryKey: featureRequestKeys.boardAll });
    void queryClient.invalidateQueries({ queryKey: featureRequestKeys.counts });
  }
}

/** Send a chat message; developers may mark it internal (hidden from the requester). */
export function useSendFeatureRequestMessage(side: FeatureRequestSide, number: string) {
  const queryClient = useQueryClient();
  return useMutation<FeatureRequestComment, Error, { body: string; isInternal?: boolean }>({
    mutationFn: ({ body, isInternal }) =>
      side === 'mine' ? sendMyMessage(number, body) : sendBoardMessage(number, body, Boolean(isInternal)),
    onSuccess: (message) => {
      queryClient.setQueryData<FeatureRequestComment[]>(featureRequestKeys.thread(side, number), (held) =>
        mergeMessages(held ?? [], [message]),
      );
      refreshAfterChange(queryClient, side, number);
    },
  });
}

// -- developer board -------------------------------------------------------------

export function useFeatureBoard(filters: FeatureBoardFilters) {
  return useQuery<PaginatedFeatureRequests>({
    queryKey: featureRequestKeys.board(filters),
    queryFn: ({ signal }) => fetchBoard(filters, signal),
    placeholderData: keepPreviousData,
    staleTime: 15 * 1000,
  });
}

/** Board badge numbers (no polling: refreshed on focus and after board actions); only for developers. */
export function useFeatureBoardCounts(enabled: boolean) {
  return useQuery<FeatureBoardCounts>({
    queryKey: featureRequestKeys.counts,
    queryFn: ({ signal }) => fetchBoardCounts(signal),
    enabled,
    refetchOnWindowFocus: true,
    staleTime: 15 * 1000,
    retry: false,
  });
}

export function useDevelopers() {
  return useQuery<FeatureRequestPerson[]>({
    queryKey: featureRequestKeys.developers,
    queryFn: ({ signal }) => fetchDevelopers(signal),
    staleTime: 5 * 60 * 1000,
  });
}

export type BoardAction =
  | { kind: 'pick' }
  | { kind: 'update'; status?: OpenFeatureRequestStatus; priority?: FeatureRequestPriority; assigneeId?: number | null }
  | { kind: 'close'; outcome: 'complete' | 'decline'; note: string };

/** Pick up, move, reassign, complete or decline a request from the board. */
export function useBoardAction(number: string) {
  const queryClient = useQueryClient();
  return useMutation<FeatureRequestDetail, Error, BoardAction>({
    mutationFn: (action) => {
      if (action.kind === 'pick') return pickBoardRequest(number);
      if (action.kind === 'close') return closeBoardRequest(number, action.outcome, action.note);
      const payload: Parameters<typeof updateBoardRequest>[1] = {};
      if (action.status) payload.status = action.status;
      if (action.priority) payload.priority = action.priority;
      if (action.assigneeId !== undefined) payload.assignee_id = action.assigneeId;
      return updateBoardRequest(number, payload);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(featureRequestKeys.detail('board', number), updated);
      refreshAfterChange(queryClient, 'board', number);
    },
  });
}
